import { FC, useState } from "react"
import {
  Check,
  X,
  SkipForward,
  ChevronDown,
  ChevronRight,
  Building2,
  Landmark,
  UserRound,
  Sliders,
  Layers,
  Workflow,
  Boxes,
  CreditCard
} from "lucide-react"
import {
  ENROLLMENT_STEP_LABELS,
  ENROLLMENT_STEP_ORDER,
  EnrollmentResponse,
  EnrollmentStep,
  EnrollmentStepName,
  StepStatus
} from "../../enroll/types"

const STEP_ICONS: Record<EnrollmentStepName, React.ReactNode> = {
  org: <Building2 size={16} />,
  brand: <Landmark size={16} />,
  lo: <UserRound size={16} />,
  rateflow: <Sliders size={16} />,
  profiles: <Layers size={16} />,
  workflow: <Workflow size={16} />,
  embeds: <Boxes size={16} />,
  billing: <CreditCard size={16} />
}

const STATUS_PILL: Record<
  StepStatus,
  { bg: string; text: string; label: string }
> = {
  success: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Success" },
  failed: { bg: "bg-red-100", text: "text-red-700", label: "Failed" },
  started: { bg: "bg-blue-100", text: "text-blue-700", label: "Running" },
  pending: { bg: "bg-neutral-100", text: "text-neutral-500", label: "Pending" },
  skipped: { bg: "bg-neutral-100", text: "text-neutral-400", label: "Skipped" }
}

const formatId = (
  step: EnrollmentStep,
  stepName: EnrollmentStepName
): string | null => {
  if (step.status !== "success" || step.id == null) return null
  if (stepName === "profiles") return `${step.id} profiles`
  if (stepName === "embeds") return `${step.id} embeds`
  return `#${step.id}`
}

const BillingDetails: FC<{ details: Record<string, unknown> }> = ({
  details
}) => {
  const rows: Array<{ label: string; value: string }> = []
  if (details.stripe_customer_id) {
    rows.push({
      label: "Stripe Customer",
      value: String(details.stripe_customer_id)
    })
  }
  if (details.level) {
    rows.push({ label: "Level", value: String(details.level) })
  }
  if (details.has_setup_fee !== undefined) {
    rows.push({
      label: "Setup Fee",
      value: details.has_setup_fee ? "Yes" : "No"
    })
  }
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      {rows.map(row => (
        <div key={row.label} className="contents">
          <span className="text-neutral-500">{row.label}</span>
          <span className="font-mono text-neutral-700">{row.value}</span>
        </div>
      ))}
    </div>
  )
}

const StepRow: FC<{
  stepName: EnrollmentStepName
  step: EnrollmentStep
}> = ({ stepName, step }) => {
  const [expanded, setExpanded] = useState(false)
  const pill = STATUS_PILL[step.status]
  const idLabel = formatId(step, stepName)
  const hasDetails = step.details && Object.keys(step.details).length > 0

  const indicator = (() => {
    if (step.status === "success") {
      return (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white">
          <Check size={14} strokeWidth={3} />
        </span>
      )
    }
    if (step.status === "failed") {
      return (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white">
          <X size={14} strokeWidth={3} />
        </span>
      )
    }
    if (step.status === "skipped") {
      return (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-300 text-white">
          <SkipForward size={14} />
        </span>
      )
    }
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-neutral-300 text-neutral-400">
        {STEP_ICONS[stepName]}
      </span>
    )
  })()

  return (
    <li className="border-b border-neutral-100 last:border-b-0">
      <button
        type="button"
        onClick={() =>
          (hasDetails || step.error_message) && setExpanded(!expanded)
        }
        className={`w-full px-4 py-3 flex items-center gap-3 text-left ${
          hasDetails || step.error_message
            ? "cursor-pointer hover:bg-neutral-50"
            : "cursor-default"
        }`}
      >
        {indicator}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-neutral-900">
            {ENROLLMENT_STEP_LABELS[stepName]}
          </span>
          {idLabel && (
            <span className="ml-2 text-xs font-mono text-neutral-500">
              {idLabel}
            </span>
          )}
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${pill.bg} ${pill.text}`}
        >
          {pill.label}
        </span>
        {(hasDetails || step.error_message) && (
          <span className="text-neutral-400">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-3 pl-14 space-y-2">
          {step.error_message && (
            <p className="text-xs text-red-700">{step.error_message}</p>
          )}
          {step.error && (
            <p className="text-xs font-mono text-neutral-500">
              Error code: {step.error}
            </p>
          )}
          {hasDetails &&
            (stepName === "billing" ? (
              <BillingDetails details={step.details!} />
            ) : (
              <pre className="text-xs font-mono text-neutral-600 bg-neutral-50 rounded p-2 overflow-x-auto max-h-48">
                {JSON.stringify(step.details, null, 2)}
              </pre>
            ))}
        </div>
      )}
    </li>
  )
}

interface StepGridProps {
  enrollment: EnrollmentResponse
}

const StepGrid: FC<StepGridProps> = ({ enrollment }) => (
  <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
    <ol>
      {ENROLLMENT_STEP_ORDER.map(name => (
        <StepRow
          key={name}
          stepName={name}
          step={
            enrollment.steps[name] ?? {
              status: "pending",
              id: null,
              error: null,
              details: null
            }
          }
        />
      ))}
    </ol>
  </div>
)

export default StepGrid
